import * as IF from "infrontjs";

import { _lcs } from "../i18n";

// Registers the <app-layout> custom element (the admin shell).
import "./../components/general/app-layout";

export class View extends IF.View
{
    constructor( appInstance )
    {
        super( appInstance );
    }

    init()
    {
        // Render the layout host once. <app-layout> decides auth vs in-app from app.user.
        this.render( this.app.container, '<app-layout></app-layout>' );

        // Keep the topbar heading in sync with the active state.
        this.app.addEventListener( IF.CustomEvents.TYPE.AFTER_STATE_CHANGE, () =>
        {
            this.updateContentHeader();
            window.scrollTo( { top: 0, behavior: "smooth" } );
        } );

        if ( this.app.user )
        {
            this.switchToInApp();
        }
        else
        {
            this.switchToAuth();
        }
    }

    switchToInApp()
    {
        this.applayout = this.app.container.querySelector( "app-layout" );
        if ( this.applayout )
        {
            this.applayout.render();
            this.pageContent = this.app.container.querySelector( 'section[data-id="page-content"]' );
            this.topbarTitle = this.app.container.querySelector( ".topbar__title" );
            this.topbarSub   = this.app.container.querySelector( ".topbar__sub" );
            this.updateContentHeader();
        }
    }

    switchToAuth()
    {
        this.applayout = this.app.container.querySelector( "app-layout" );
        if ( this.applayout )
        {
            this.applayout.render();
        }
        this.pageContent = null;
        this.topbarTitle = null;
        this.topbarSub = null;
    }

    updateContentHeader()
    {
        const state = this.app.stateManager.currentState;
        if ( !this.topbarTitle || !state || typeof state.getContentHeaderData !== "function" )
        {
            return;
        }
        const data = state.getContentHeaderData() || {};
        this.topbarTitle.textContent = data.title || "";
        if ( this.topbarSub )
        {
            this.topbarSub.textContent = data.subtitle || "";
        }
    }

    /**
     * Generic toast notification. Vanilla (no runtime dependency) — styled via
     * toast.css on the BELTURO tokens, tinted by currentColor icons.
     *
     * @param {string} message  Text to display (rendered as text, not HTML).
     * @param {('success'|'error'|'info'|'warning')} [type='success']
     * @param {number} [duration=4000]  Auto-dismiss after ms (click dismisses too).
     * @returns {HTMLElement} the toast element.
     */
    toast( message, type = "success", duration = 4000 )
    {
        const ICONS = {
            success : "check",
            error   : "triangle-alert",
            warning : "triangle-alert",
            info    : "bell"
        };

        const container = this._ensureToastContainer();

        const el = document.createElement( "div" );
        el.className = "toast toast--" + type;
        el.setAttribute( "role", type === "error" ? "alert" : "status" );

        const icon = document.createElement( "i" );
        icon.className = "icon icon--" + ( ICONS[ type ] || ICONS.info );

        const msg = document.createElement( "span" );
        msg.className = "toast__msg";
        msg.textContent = message;

        el.append( icon, msg );
        container.appendChild( el );

        // Animate in on the next frame.
        requestAnimationFrame( () => el.classList.add( "toast--in" ) );

        let removed = false;
        const remove = () =>
        {
            if ( removed ) return;
            removed = true;
            clearTimeout( timer );
            el.classList.remove( "toast--in" );
            el.addEventListener( "transitionend", () => el.remove(), { once: true } );
            // Fallback in case the transition never fires.
            setTimeout( () => el.remove(), 400 );
        };

        const timer = setTimeout( remove, duration );
        el.addEventListener( "click", remove );

        return el;
    }

    _ensureToastContainer()
    {
        let container = document.querySelector( ".toast-container" );
        if ( !container )
        {
            container = document.createElement( "div" );
            container.className = "toast-container";
            document.body.appendChild( container );
        }
        return container;
    }

    /**
     * Generic confirmation dialog. Resolves true on confirm, false on
     * cancel / backdrop / Escape. Vanilla (no dependency) — styled via the
     * shared .modal styles on the BELTURO tokens.
     *
     * @param {string} title
     * @param {string|null} [text]
     * @param {string} [confirmLabel="OK"]
     * @param {string} [cancelLabel]  Defaults to the translated "Abbrechen".
     * @param {{ danger?: boolean }} [opts]
     * @returns {Promise<boolean>}
     */
    confirm( title, text = null, confirmLabel = "OK", cancelLabel = _lcs( "Abbrechen" ), opts = {} )
    {
        return new Promise( ( resolve ) =>
        {
            const overlay = document.createElement( "div" );
            overlay.className = "modal modal--confirm";

            const confirmClass = opts.danger ? "btn btn--danger btn--sm" : "btn btn--primary btn--sm";

            overlay.innerHTML =
                '<div class="modal__backdrop" data-id="c-backdrop"></div>' +
                '<div class="modal__dialog modal__dialog--sm" role="alertdialog" aria-modal="true">' +
                    '<header class="modal__header"><h2 class="modal__title"></h2></header>' +
                    '<div class="modal__body"><p class="modal__text"></p></div>' +
                    '<footer class="modal__footer">' +
                        '<button type="button" class="btn btn--ghost btn--sm" data-id="c-cancel"></button>' +
                        '<button type="button" class="' + confirmClass + '" data-id="c-confirm"></button>' +
                    '</footer>' +
                '</div>';

            // textContent assignment avoids HTML injection from caller strings.
            overlay.querySelector( ".modal__title" ).textContent = title;
            const textEl = overlay.querySelector( ".modal__text" );
            if ( text )
            {
                textEl.textContent = text;
            }
            else
            {
                textEl.remove();
            }
            overlay.querySelector( '[data-id="c-cancel"]' ).textContent = cancelLabel;
            overlay.querySelector( '[data-id="c-confirm"]' ).textContent = confirmLabel;

            document.body.appendChild( overlay );

            const onKeydown = ( e ) =>
            {
                if ( e.key === "Escape" ) settle( false );
            };

            const settle = ( answer ) =>
            {
                document.removeEventListener( "keydown", onKeydown );
                overlay.remove();
                resolve( answer );
            };

            overlay.querySelector( '[data-id="c-confirm"]' ).addEventListener( "click", () => settle( true ) );
            overlay.querySelector( '[data-id="c-cancel"]' ).addEventListener( "click", () => settle( false ) );
            overlay.querySelector( '[data-id="c-backdrop"]' ).addEventListener( "click", () => settle( false ) );
            document.addEventListener( "keydown", onKeydown );

            overlay.querySelector( '[data-id="c-confirm"]' ).focus();
        } );
    }
}
